<template>
    <div class="terms-page">
        <PageSpinner :onloader="onloader" />
        <base-header type="gradient-info" class="pb-6 pb-8 pt-5 pt-md-8">
            <router-link to="/dashboard" class="text-white">
                Dashboard
            </router-link>
            <router-link to="/school/academic-management" class="text-white">
                <i class="fa fa-ellipsis-v px-2"></i>Academic Management
            </router-link>
            <span class="text-white"><i class="fa fa-ellipsis-v px-2"></i>Academic Terms</span>
        </base-header>

        <div class="container-fluid mt--7 terms-page__body">
            <section class="terms-panel" aria-labelledby="terms-page-title">
                <header class="terms-panel__intro">
                    

                    <div class="terms-panel__side">
                        <div v-if="currentTerm" class="terms-current">
                            <span class="terms-current__label">CURRENT TERM</span>
                            <span class="terms-current__value">{{ currentTermLabel }}</span>
                        </div>
                        <button type="button" class="btn btn-primary" @click="openCreateForm">
                            <i class="fa fa-plus" aria-hidden="true"></i>
                            <span>New term</span>
                        </button>
                    </div>
                </header>
                <header class="terms-toolbar">
                    <div class="terms-toolbar__title">
                        <h2 id="terms-list-title">Registered terms</h2>
                        <p v-if="resultsLabel" class="terms-toolbar__count">{{ resultsLabel }}</p>
                        <p v-else class="terms-toolbar__count">
                            Terms registered against a session appear here.
                        </p>
                    </div>

                    <div class="terms-search">
                        <i class="fa fa-search" aria-hidden="true"></i>
                        <input
                            v-model="searchval"
                            type="search"
                            class="terms-search__input"
                            placeholder="Search session or term"
                            aria-label="Search registered terms by session or term"
                        />
                        <button
                            v-if="searchval"
                            type="button"
                            class="terms-search__clear"
                            aria-label="Clear search"
                            @click="searchval = ''"
                        >
                            <i class="fa fa-times" aria-hidden="true"></i>
                        </button>
                    </div>
                </header>

                <div v-if="!registerdterm" class="terms-state">
                    <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
                    <strong>Loading registered terms</strong>
                    <span>This only takes a moment.</span>
                </div>

                <div v-else-if="!terms.length" class="terms-state">
                    <i class="fa fa-calendar-o" aria-hidden="true"></i>
                    <strong>No terms registered yet</strong>
                    <span>Register the first term to start building the academic calendar.</span>
                    <button type="button" class="btn terms-ghost-button" @click="openCreateForm">
                        <i class="fa fa-plus" aria-hidden="true"></i>
                        <span>Register a term</span>
                    </button>
                </div>

                <div v-else-if="!searchedResultQuery.length" class="terms-state">
                    <i class="fa fa-search" aria-hidden="true"></i>
                    <strong>No terms match “{{ searchval }}”</strong>
                    <span>Try a different session or term name.</span>
                    <button type="button" class="btn terms-ghost-button" @click="searchval = ''">
                        Clear search
                    </button>
                </div>

                <template v-else>
                    <div class="terms-table-wrap">
                        <table class="terms-table">
                            <caption class="sr-only">Registered academic terms</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Term</th>
                                    <th scope="col">Session</th>
                                    <th scope="col" class="is-center">Mid-term opens</th>
                                    <th scope="col">Next mid-term begins</th>
                                    <th scope="col" class="is-center">Full-term opens</th>
                                    <th scope="col">Next full term begins</th>
                                    <th scope="col" class="terms-table__action-head">
                                        <span class="sr-only">Actions</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="item in searchedResultQuery"
                                    :key="item.Id"
                                    :class="{ 'is-current': isCurrentTerm(item) }"
                                >
                                    <td>
                                        <div class="terms-table__identity">
                                            <span class="terms-table__term">{{ item.SchTerm || "—" }}</span>
                                            <span v-if="isCurrentTerm(item)" class="terms-badge">Current</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span class="terms-table__session">{{ item.SchSession || "—" }}</span>
                                    </td>
                                    <td class="is-center terms-table__count">{{ formatCount(item.MidTermOpenNo) }}</td>
                                    <td class="terms-table__date">{{ formatDate(item.NextMidTermBeging) }}</td>
                                    <td class="is-center terms-table__count">{{ formatCount(item.FullTermOpenNo) }}</td>
                                    <td class="terms-table__date">{{ formatDate(item.NextTermBeging) }}</td>
                                    <td class="terms-table__action">
                                        <button
                                            type="button"
                                            class="terms-edit"
                                            :aria-label="editLabel(item)"
                                            @click="edititem(item.Id)"
                                        >
                                            <i class="fa fa-pencil" aria-hidden="true"></i>
                                            <span>Edit</span>
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="terms-cards">
                        <article
                            v-for="item in searchedResultQuery"
                            :key="item.Id"
                            class="term-card"
                            :class="{ 'term-card--current': isCurrentTerm(item) }"
                        >
                            <div class="term-card__head">
                                <div class="term-card__identity">
                                    <span class="term-card__term">{{ item.SchTerm || "—" }}</span>
                                    <span class="term-card__session">{{ item.SchSession || "—" }}</span>
                                </div>
                                <span v-if="isCurrentTerm(item)" class="terms-badge">Current</span>
                            </div>

                            <dl class="term-card__facts">
                                <div>
                                    <dt>Mid-term opens</dt>
                                    <dd>{{ formatCount(item.MidTermOpenNo) }}</dd>
                                </div>
                                <div>
                                    <dt>Next mid-term</dt>
                                    <dd>{{ formatDate(item.NextMidTermBeging) }}</dd>
                                </div>
                                <div>
                                    <dt>Full-term opens</dt>
                                    <dd>{{ formatCount(item.FullTermOpenNo) }}</dd>
                                </div>
                                <div>
                                    <dt>Next full term</dt>
                                    <dd>{{ formatDate(item.NextTermBeging) }}</dd>
                                </div>
                            </dl>

                            <div class="term-card__actions">
                                <button
                                    type="button"
                                    class="btn terms-ghost-button"
                                    :aria-label="editLabel(item)"
                                    @click="edititem(item.Id)"
                                >
                                    <i class="fa fa-pencil" aria-hidden="true"></i>
                                    <span>Edit term</span>
                                </button>
                            </div>
                        </article>
                    </div>
                </template>
            </section>
        </div>

        <modal
            :show.sync="formVisible"
            modal-classes="modal-dialog-centered"
            @close="handleFormClosed"
        >
            <div class="terms-drawer">
                <header class="terms-drawer__head">
                    <span class="terms-drawer__icon" aria-hidden="true">
                        <i class="fa" :class="process === 'U' ? 'fa-pencil' : 'fa-calendar-plus-o'"></i>
                    </span>
                    <div class="terms-drawer__heading">
                        <span class="terms-drawer__eyebrow">
                            {{ process === "U" ? "UPDATE RECORD" : "NEW RECORD" }}
                        </span>
                        <h2>{{ process === "U" ? "Edit term" : "Register a term" }}</h2>
                        <p v-if="editingLabel">{{ editingLabel }}</p>
                        <p v-else>Attach a term to a session and set its calendar.</p>
                    </div>
                    <button
                        type="button"
                        class="terms-drawer__close"
                        aria-label="Close"
                        @click="closeForm"
                    >
                        <i class="fa fa-times" aria-hidden="true"></i>
                    </button>
                </header>

                <form id="term-form" class="terms-form" novalidate @submit.prevent="submitdata">
                    <fieldset class="terms-fieldset">
                        <legend>Term details</legend>
                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="term-session">
                                        Session <span class="terms-required">*</span>
                                    </label>
                                    <select
                                        id="term-session"
                                        v-model="session"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.session }"
                                        :disabled="inputdisable || isSaving"
                                        @change="clearError('session')"
                                    >
                                        <option v-if="!SessionItems" :value="0" disabled>Loading sessions…</option>
                                        <option v-else :value="0">Select session</option>
                                        <option v-for="item in SessionItems" :key="item.Id" :value="item.Id">
                                            {{ item.SchSession }}
                                        </option>
                                    </select>
                                    <span v-if="errors.session" class="terms-error" role="alert">
                                        {{ errors.session }}
                                    </span>
                                    <span v-else-if="inputdisable" class="terms-hint">
                                        The session is fixed for an existing term record.
                                    </span>
                                </div>
                            </div>

                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="term-name">
                                        Term <span class="terms-required">*</span>
                                    </label>
                                    <select
                                        id="term-name"
                                        v-model="term"
                                        class="form-control"
                                        :class="{ 'is-invalid': errors.term }"
                                        :disabled="inputdisable || isSaving"
                                        @change="clearError('term')"
                                    >
                                        <option v-if="!schterm" :value="0" disabled>Loading terms…</option>
                                        <option v-else :value="0">Select term</option>
                                        <option v-for="item in schterm" :key="item.Id" :value="item.Id">
                                            {{ item.SchTerm }}
                                        </option>
                                    </select>
                                    <span v-if="errors.term" class="terms-error" role="alert">
                                        {{ errors.term }}
                                    </span>
                                    <span v-else-if="inputdisable" class="terms-hint">
                                        The term is fixed for an existing term record.
                                    </span>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <fieldset class="terms-fieldset">
                        <legend>Term calendar</legend>
                        <p class="terms-fieldset__note">
                            These dates and opening counts are used on report cards and other
                            term-based screens.
                        </p>
                        <div class="row">
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="next-mid-term">Next mid-term begins</label>
                                    <input
                                        id="next-mid-term"
                                        v-model="nextmidtermbegin"
                                        type="date"
                                        class="form-control"
                                        :disabled="isSaving"
                                    />
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="mid-term-open">Mid-term school openings</label>
                                    <input
                                        id="mid-term-open"
                                        v-model.number="nooftimemidtermopen"
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputmode="numeric"
                                        class="form-control"
                                        :disabled="isSaving"
                                    />
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="next-full-term">Next full term begins</label>
                                    <input
                                        id="next-full-term"
                                        v-model="nexttermbegin"
                                        type="date"
                                        class="form-control"
                                        :disabled="isSaving"
                                    />
                                </div>
                            </div>
                            <div class="col-sm-6">
                                <div class="form-group">
                                    <label for="full-term-open">Full-term school openings</label>
                                    <input
                                        id="full-term-open"
                                        v-model.number="nooftimeopen"
                                        type="number"
                                        min="0"
                                        step="1"
                                        inputmode="numeric"
                                        class="form-control"
                                        :disabled="isSaving"
                                    />
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    <div class="terms-current-toggle" :class="{ 'is-active': iscurrent }">
                        <base-checkbox v-model="iscurrent" :disabled="isSaving">
                            Make this the current term
                        </base-checkbox>
                        <p>
                            Students and staff will see this term as the active term across the
                            portal. Only one term is current at a time.
                        </p>
                    </div>
                </form>
            </div>

            <template slot="footer">
                <div class="terms-drawer__actions">
                    <button
                        type="button"
                        class="btn btn-outline-secondary"
                        :disabled="isSaving"
                        @click="closeForm"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        form="term-form"
                        class="btn terms-submit"
                        :disabled="isSaving"
                    >
                        <i
                            class="fa"
                            :class="isSaving ? 'fa-spinner fa-spin' : 'fa-check'"
                            aria-hidden="true"
                        ></i>
                        <span>{{ submitLabel }}</span>
                    </button>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
import moment from "moment";
import Alert from "@/mixins/Alert.mixins";
import PageSpinner from "@/components/PageSpinner";

const CURRENT_TRUE_VALUES = ["true", "1", "y", "yes"];

export default {
    name: "term",
    mixins: [Alert],
    components: {
        PageSpinner
    },
    data() {
        return {
            accessDetails: null,
            session: 0,
            term: 0,
            nextmidtermbegin: "",
            nooftimemidtermopen: "",
            nexttermbegin: "",
            nooftimeopen: "",
            iscurrent: false,
            examscore: "",
            cascore: "",
            midtermscore: "",
            topicscore: "",
            registerdterm: null,
            SessionItems: null,
            schterm: null,
            process: "N",
            inputdisable: false,
            formVisible: false,
            isSaving: false,
            searchval: "",
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        terms() {
            return Array.isArray(this.registerdterm) ? this.registerdterm : [];
        },
        currentTerm() {
            return this.terms.find(item => this.isCurrentTerm(item)) || null;
        },
        currentTermLabel() {
            const current = this.currentTerm;
            if (!current) return "";
            return [current.SchTerm, current.SchSession].filter(Boolean).join(" · ");
        },
        selectedSessionName() {
            const item = (this.SessionItems || []).find(
                session => String(session.Id) === String(this.session)
            );
            return item ? item.SchSession : "";
        },
        selectedTermName() {
            const item = (this.schterm || []).find(
                entry => String(entry.Id) === String(this.term)
            );
            return item ? item.SchTerm : "";
        },
        editingLabel() {
            if (this.process !== "U") return "";
            return [this.selectedTermName, this.selectedSessionName].filter(Boolean).join(" · ");
        },
        submitLabel() {
            if (this.isSaving) return "Saving…";
            return this.process === "U" ? "Save changes" : "Register term";
        },
        searchedResultQuery() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.terms;

            return this.terms.filter(item => {
                const haystack = [
                    item.SchTerm,
                    item.SchSession,
                    this.formatDate(item.NextMidTermBeging),
                    this.formatDate(item.NextTermBeging)
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();

                return tokens.every(token => haystack.includes(token));
            });
        },
        resultsLabel() {
            const total = this.terms.length;
            if (!total) return "";
            if (this.searchval.trim()) {
                return `${this.searchedResultQuery.length} of ${total} shown`;
            }
            return `${total} ${total === 1 ? "term registered" : "terms registered"}`;
        }
    },
    methods: {
        hasValue(value) {
            return value !== null && value !== undefined && value !== "" && value !== 0 && value !== "0";
        },
        isCurrentTerm(item) {
            const value = item && item.IsCurrent;
            if (typeof value === "boolean") return value;
            if (typeof value === "number") return value === 1;
            if (typeof value === "string") {
                return CURRENT_TRUE_VALUES.includes(value.trim().toLowerCase());
            }
            return false;
        },
        formatDate(value) {
            if (!value) return "—";
            if (typeof value === "string" && value.startsWith("0001-01-01")) return "—";
            const parsed = moment(value);
            return parsed.isValid() ? parsed.format("DD MMM YYYY") : "—";
        },
        toDateInput(value) {
            if (!value) return "";
            if (typeof value === "string" && value.startsWith("0001-01-01")) return "";
            const parsed = moment(value);
            return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
        },
        formatCount(value) {
            if (value === null || value === undefined || value === "") return "—";
            return String(value);
        },
        editLabel(item) {
            const label = [item && item.SchTerm, item && item.SchSession].filter(Boolean).join(" ");
            return label ? `Edit ${label}` : "Edit term";
        },
        errorMessage(err, fallback) {
            const data = err && err.response && err.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (err && err.message) return err.message;
            return fallback;
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validateForm() {
            const errors = {};
            if (!this.hasValue(this.session)) {
                errors.session = "Choose the session this term belongs to.";
            }
            if (!this.hasValue(this.term)) {
                errors.term = "Choose the term you want to register.";
            }
            this.errors = errors;
            return Object.keys(errors).length === 0;
        },
        resetForm() {
            this.session = 0;
            this.term = 0;
            this.nextmidtermbegin = "";
            this.nooftimemidtermopen = "";
            this.nexttermbegin = "";
            this.nooftimeopen = "";
            this.iscurrent = false;
            this.examscore = "";
            this.cascore = "";
            this.midtermscore = "";
            this.topicscore = "";
            this.process = "N";
            this.inputdisable = false;
            this.errors = {};
        },
        openCreateForm() {
            this.resetForm();
            this.formVisible = true;
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        handleFormClosed() {
            this.resetForm();
        },
        handleKeydown(event) {
            if (event.key !== "Escape") return;
            if (!this.formVisible || this.isSaving) return;
            this.closeForm();
        },
        async loadSessions() {
            try {
                const sessions = await this.$store.dispatch("getsession");
                this.SessionItems = Array.isArray(sessions) ? sessions : [];
            } catch (err) {
                this.SessionItems = [];
                this.ShowAlertError(
                    this.errorMessage(err, "Could not load the school sessions."),
                    ""
                );
            }
        },
        async getterm() {
            try {
                const terms = await this.$store.dispatch("getterm");
                this.schterm = Array.isArray(terms) ? terms : [];
            } catch (err) {
                this.schterm = [];
                this.ShowAlertError(this.errorMessage(err, "Could not load the term list."), "");
            }
        },
        async getregisteredterm() {
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getregisteredterm");
                this.registerdterm = Array.isArray(resp) ? resp : [];
            } catch (err) {
                console.error(err);
                await this.$store.dispatch("auth/Logout");
                this.$router.push("/login");
            } finally {
                this.onloader = "none";
            }
        },
        async edititem(Id) {
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getregisteredtermbyid", Id);
                if (!resp) throw new Error("The selected term could not be loaded.");

                this.session = resp.SessionId || 0;
                this.term = resp.TermId || 0;
                this.nextmidtermbegin = this.toDateInput(resp.NextMidTermBeging);
                this.nexttermbegin = this.toDateInput(resp.NextTermBeging);
                this.nooftimemidtermopen = this.formatCount(resp.MidTermOpenNo) === "—"
                    ? ""
                    : resp.MidTermOpenNo;
                this.nooftimeopen = this.formatCount(resp.FullTermOpenNo) === "—"
                    ? ""
                    : resp.FullTermOpenNo;
                this.iscurrent = this.isCurrentTerm(resp);
                this.examscore = resp.ExamScore || "";
                this.cascore = resp.CAScore || "";
                this.midtermscore = resp.MidTermScore === null || resp.MidTermScore === undefined
                    ? ""
                    : resp.MidTermScore;
                this.topicscore = resp.TopicScore === null || resp.TopicScore === undefined
                    ? ""
                    : resp.TopicScore;
                this.process = "U";
                this.inputdisable = true;
                this.errors = {};
                this.formVisible = true;
            } catch (err) {
                console.error(err);
                this.ShowAlertError(
                    this.errorMessage(err, "Could not load this term record."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        async submitdata() {
            if (this.isSaving) return;
            if (!this.validateForm()) return;

            const wasCurrent = this.iscurrent;
            const payload = {
                sessionid: this.session,
                termid: this.term,
                midtermnexttermnegin: this.nextmidtermbegin || null,
                midtermnoschopen:
                    this.hasValue(this.nooftimemidtermopen) || this.nooftimemidtermopen === 0
                        ? this.nooftimemidtermopen
                        : null,
                NextTermBegin: this.nexttermbegin || null,
                NoofTimeSchOpen:
                    this.hasValue(this.nooftimeopen) || this.nooftimeopen === 0
                        ? this.nooftimeopen
                        : null,
                ExamScore: this.examscore,
                CAScore: this.cascore,
                MidtermScore: this.midtermscore,
                TopicScore: this.topicscore,
                IsCurrent: this.iscurrent,
                processop: this.process,
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("termregistration", payload);
                if (Array.isArray(resp)) {
                    this.registerdterm = resp;
                } else {
                    await this.getregisteredterm();
                }
                this.closeForm();
                this.ShowAlertSuccess(
                    wasCurrent
                        ? "Term saved. It is now the current term."
                        : "Term saved successfully.",
                    ""
                );
            } catch (err) {
                this.ShowAlertError(
                    this.errorMessage(err, "The term could not be saved."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = (this.accessDetails = this.$store.getters["auth/_user"]);
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }

        document.addEventListener("keydown", this.handleKeydown);
        this.loadSessions();
        this.getregisteredterm();
        this.getterm();
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleKeydown);
    }
};
</script>

<style scoped>
.terms-page {
    min-height: 100%;
}

.terms-page__body {
    padding-bottom: 2.5rem;
}

.terms-panel {
    position: relative;
    overflow: hidden;
    border: 1px solid #e5eaf0;
    border-radius: 0.9rem;
    background: #fff;
    box-shadow: 0 0.6rem 1.75rem rgba(50, 70, 90, 0.1);
}

.terms-panel__intro {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1.25rem;
    padding: 1.5rem 1.5rem 1.35rem;
    border-bottom: 1px solid #edf0f3;
}

.terms-panel__text {
    min-width: 0;
}

.terms-eyebrow {
    display: block;
    margin-bottom: 0.35rem;
    color: #8492a2;
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.11em;
}

.terms-panel__intro h1 {
    margin: 0;
    color: #293b52;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.2;
}

.terms-panel__intro p {
    max-width: 46rem;
    margin: 0.45rem 0 0;
    color: #7d8b9a;
    font-size: 0.88rem;
    line-height: 1.55;
}

.terms-panel__side {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.75rem;
}

.terms-current {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid #bcd4ef;
    border-radius: 0.65rem;
    background: #f5f9ff;
}

.terms-current__label {
    color: #5b86b5;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.09em;
}

.terms-current__value {
    color: #1b4f8f;
    font-size: 0.8rem;
    font-weight: 700;
    white-space: nowrap;
}

.terms-add {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    padding: 0.62rem 1.05rem;
    border: 1px solid #1850b8;
    border-radius: 0.55rem;
    background: #1850b8;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 600;
    line-height: 1.2;
    box-shadow: 0 0.35rem 0.9rem rgba(24, 80, 184, 0.2);
    transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

.terms-add:hover,
.terms-add:focus {
    background: #144a9e;
    border-color: #144a9e;
    color: #fff;
    box-shadow: 0 0.45rem 1.1rem rgba(24, 80, 184, 0.26);
}

.terms-add:focus-visible {
    outline: 2px solid #1850b8;
    outline-offset: 2px;
}
.terms-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.15rem 1.5rem;
    border-bottom: 1px solid #edf0f3;
}

.terms-toolbar h2 {
    margin: 0;
    color: #344767;
    font-size: 1.05rem;
    font-weight: 700;
}

.terms-toolbar__count {
    margin: 0.3rem 0 0;
    color: #8490a0;
    font-size: 0.76rem;
}

.terms-search {
    position: relative;
    display: flex;
    flex: 0 1 280px;
    align-items: center;
    min-width: 220px;
}

.terms-search > i {
    position: absolute;
    left: 0.8rem;
    color: #9aa6b3;
    font-size: 0.8rem;
    pointer-events: none;
}

.terms-search__input {
    width: 100%;
    height: 2.45rem;
    padding: 0.4rem 2.25rem 0.4rem 2.15rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.55rem;
    background: #fff;
    color: #344767;
    font-size: 0.82rem;
    -webkit-appearance: none;
}

.terms-search__input::placeholder {
    color: #a2adba;
}

.terms-search__input::-webkit-search-cancel-button {
    display: none;
}

.terms-search__input:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.terms-search__clear {
    position: absolute;
    right: 0.4rem;
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #9aa6b3;
    font-size: 0.72rem;
    cursor: pointer;
}

.terms-search__clear:hover,
.terms-search__clear:focus {
    background: #eef2f6;
    color: #526273;
    outline: none;
}

.terms-state {
    display: flex;
    min-height: 230px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    padding: 2rem 1.5rem;
    color: #83909e;
    text-align: center;
}

.terms-state > i {
    margin-bottom: 0.35rem;
    color: #aab6c2;
    font-size: 1.6rem;
}

.terms-state strong {
    color: #526273;
    font-size: 0.95rem;
}

.terms-state span {
    max-width: 26rem;
    font-size: 0.8rem;
    line-height: 1.55;
}

.terms-state .terms-ghost-button {
    margin-top: 0.75rem;
}

.terms-table-wrap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

.terms-table {
    width: 100%;
    min-width: 820px;
    margin: 0;
    border-collapse: separate;
    border-spacing: 0;
}

.terms-table th {
    padding: 0.7rem 1.5rem;
    border-bottom: 1px solid #e8edf3;
    background: #f8fafc;
    color: #7b8998;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-align: left;
    text-transform: uppercase;
    white-space: nowrap;
}

.terms-table td {
    padding: 0.85rem 1.5rem;
    border-bottom: 1px solid #edf0f3;
    color: #526273;
    font-size: 0.84rem;
    vertical-align: middle;
}

.terms-table tbody tr:last-child td {
    border-bottom: 0;
}

.terms-table tbody tr:hover {
    background: #fafbfd;
}

.terms-table tbody tr.is-current {
    background: #f5f9ff;
}

.terms-table tbody tr.is-current:hover {
    background: #eef4ff;
}

.terms-table tbody tr.is-current td:first-child {
    box-shadow: inset 3px 0 0 #1850b8;
}

.terms-table .is-center {
    text-align: center;
}

.terms-table__action-head {
    width: 1%;
}

.terms-table__identity {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem;
}

.terms-table__term {
    overflow-wrap: anywhere;
    color: #344767;
    font-weight: 700;
}

.terms-table__session {
    overflow-wrap: anywhere;
    color: #526273;
    font-weight: 600;
}

.terms-table__count {
    color: #44566c;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.terms-table__date {
    color: #66778a;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.terms-table__action {
    width: 1%;
    text-align: right;
    white-space: nowrap;
}

.terms-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.16rem 0.45rem;
    border-radius: 999px;
    background: #eef4ff;
    color: #1850b8;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    line-height: 1.4;
    text-transform: uppercase;
    white-space: nowrap;
}

.terms-edit {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.35rem 0.6rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.terms-edit:hover,
.terms-edit:focus {
    border-color: #7ea8dd;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

.terms-cards {
    display: none;
    gap: 0.85rem;
    padding: 1.25rem 1.5rem;
}

.term-card {
    min-width: 0;
    padding: 1rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.8rem;
    background: #fff;
    box-shadow: 0 0.25rem 0.75rem rgba(50, 70, 90, 0.05);
}

.term-card--current {
    border-color: #bcd4ef;
    background: linear-gradient(180deg, #fbfefc, #f5f9ff);
}

.term-card__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem;
}

.term-card__identity {
    display: grid;
    min-width: 0;
    gap: 0.15rem;
}

.term-card__term {
    overflow-wrap: anywhere;
    color: #344767;
    font-size: 0.95rem;
    font-weight: 700;
}

.term-card__session {
    color: #7b8998;
    font-size: 0.78rem;
    font-weight: 600;
}

.term-card__facts {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.7rem 0.9rem;
    margin: 0.9rem 0 0;
    padding-top: 0.85rem;
    border-top: 1px dashed #e8edf3;
}

.term-card__facts dt {
    color: #99a4b1;
    font-size: 0.6rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.term-card__facts dd {
    margin: 0.2rem 0 0;
    overflow-wrap: anywhere;
    color: #44566c;
    font-size: 0.84rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}

.term-card__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 0.9rem;
    padding-top: 0.85rem;
    border-top: 1px solid #edf0f3;
}

.terms-ghost-button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    padding: 0.4rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.45rem;
    background: #fff;
    color: #44566c;
    font-size: 0.76rem;
    font-weight: 600;
    line-height: 1.3;
    transition: border-color 0.15s ease, color 0.15s ease, background-color 0.15s ease;
}

.terms-ghost-button:hover,
.terms-ghost-button:focus {
    border-color: #7ea8dd;
    background: #f5f9ff;
    color: #1850b8;
    outline: none;
}

/* ---------- Drawer form ---------- */

.terms-drawer {
    display: flex;
    min-height: 100%;
    flex-direction: column;
}

.terms-drawer__head {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    padding-bottom: 1.1rem;
    border-bottom: 1px solid #edf0f3;
}

.terms-drawer__icon {
    display: grid;
    flex: 0 0 2.6rem;
    width: 2.6rem;
    height: 2.6rem;
    place-items: center;
    border-radius: 0.7rem;
    background: #eef4ff;
    color: #1850b8;
    font-size: 1rem;
}

.terms-drawer__heading {
    min-width: 0;
}

.terms-drawer__eyebrow {
    display: block;
    margin-bottom: 0.15rem;
    color: #8492a2;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.1em;
}

.terms-drawer__heading h2 {
    margin: 0;
    color: #344767;
    font-size: 1.15rem;
    font-weight: 700;
    line-height: 1.25;
}

.terms-drawer__heading p {
    margin: 0.3rem 0 0;
    color: #7d8b9a;
    font-size: 0.8rem;
    line-height: 1.45;
}

.terms-drawer__close {
    display: inline-flex;
    flex: 0 0 2rem;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: #8492a2;
    font-size: 0.85rem;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.terms-drawer__close:hover,
.terms-drawer__close:focus {
    background: #eef2f6;
    color: #344767;
    outline: none;
}

.terms-form {
    margin: 0;
}

.terms-form .row {
    margin-right: -7.5px;
    margin-left: -7.5px;
}

.terms-form .row > [class*="col-"] {
    padding-right: 7.5px;
    padding-left: 7.5px;
}

.terms-fieldset {
    margin: 1.35rem 0 0;
    padding: 0;
    border: 0;
}

.terms-fieldset legend {
    display: block;
    width: auto;
    margin: 0 0 0.85rem;
    padding: 0;
    color: #8492a2;
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
}

.terms-fieldset__note {
    margin: -0.35rem 0 0.9rem;
    color: #8a97a6;
    font-size: 0.75rem;
    line-height: 1.5;
}

.terms-form .form-group {
    margin-bottom: 1rem;
}

.terms-form label {
    margin-bottom: 0.35rem;
    color: #44566c;
    font-size: 0.78rem;
    font-weight: 700;
}

.terms-required {
    color: #c23b4a;
    font-weight: 700;
}

.terms-form .form-control {
    height: 2.5rem;
    padding: 0.35rem 0.7rem;
    border: 1px solid #dbe3ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #344767;
    font-size: 0.84rem;
}

.terms-form .form-control:focus {
    border-color: #7ea8dd;
    outline: none;
    box-shadow: 0 0 0 0.2rem rgba(24, 80, 184, 0.12);
}

.terms-form .form-control:disabled {
    background: #f4f6f9;
    color: #8492a2;
    cursor: not-allowed;
}

.terms-form .form-control.is-invalid {
    border-color: #d76a76;
    box-shadow: 0 0 0 0.2rem rgba(194, 59, 74, 0.1);
}

.terms-error {
    display: block;
    margin-top: 0.3rem;
    color: #c23b4a;
    font-size: 0.72rem;
    font-weight: 600;
}

.terms-hint {
    display: block;
    margin-top: 0.3rem;
    color: #8a97a6;
    font-size: 0.72rem;
    line-height: 1.45;
}

.terms-current-toggle {
    margin-top: 1.35rem;
    padding: 1rem;
    border: 1px solid #e5eaf0;
    border-radius: 0.75rem;
    background: #f8fafc;
    transition: border-color 0.15s ease, background-color 0.15s ease;
}

.terms-current-toggle.is-active {
    border-color: #bcd4ef;
    background: #f5f9ff;
}

.terms-current-toggle ::v-deep .custom-control-label {
    color: #344767;
    font-size: 0.82rem;
    font-weight: 600;
}

.terms-current-toggle p {
    margin: 0.45rem 0 0 1.6rem;
    color: #7d8b9a;
    font-size: 0.74rem;
    line-height: 1.5;
}

.terms-drawer__actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.6rem;
    width: 100%;
}

.terms-drawer__actions .btn {
    display: inline-flex;
    min-height: 2.5rem;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    margin: 0;
    padding: 0.4rem 1.1rem;
    border-radius: 0.5rem;
    font-size: 0.82rem;
    font-weight: 600;
}

.terms-submit {
    border: 1px solid #1850b8;
    background: #1850b8;
    color: #fff;
}

.terms-submit:hover,
.terms-submit:focus {
    border-color: #144a9e;
    background: #144a9e;
    color: #fff;
}

.terms-submit:disabled {
    border-color: #b9c8dc;
    background: #b9c8dc;
    color: #fff;
    cursor: not-allowed;
}

@media (max-width: 991.98px) {
    .terms-table-wrap {
        display: none;
    }

    .terms-cards {
        display: grid;
    }
}

@media (max-width: 767.98px) {
    .terms-panel__intro {
        align-items: flex-start;
    }

    .terms-toolbar {
        flex-direction: column;
        align-items: stretch;
    }

    .terms-search {
        flex: 0 0 auto;
        width: 100%;
    }
}

@media (max-width: 575.98px) {
    .terms-page__body {
        padding-bottom: 1.5rem;
    }

    .terms-panel__intro {
        flex-direction: column;
        gap: 1rem;
        padding: 1.15rem 1rem 1rem;
    }

    .terms-panel__intro h1 {
        font-size: 1.35rem;
    }

    .terms-panel__side {
        width: 100%;
        flex-wrap: wrap;
    }

    .terms-current {
        flex: 1 1 100%;
    }

    .terms-add {
        width: 100%;
        justify-content: center;
    }

    .terms-toolbar {
        padding: 1rem;
    }

    .terms-cards {
        padding: 0.85rem;
    }

    .term-card__facts {
        gap: 0.6rem;
    }

    .terms-drawer__head {
        gap: 0.7rem;
    }

    .terms-drawer__actions {
        flex-direction: column-reverse;
    }

    .terms-drawer__actions .btn {
        width: 100%;
    }
}
</style>
